/**
 * AdminFeaturedDemosPage — /admin/featured-demos
 *
 * The curator's desk. Mode 2 functional editorial — NOT a CMS
 * dashboard, NOT a moderation queue, NOT an admin table. The page
 * exists to answer one question: "Which preserved nights should
 * represent the platform right now?"
 *
 * Three (or four) vertical editorial lanes — Schools ✦ / Year 6 ✦ /
 * Prom ✦ / Universal fallback ✦ — each showing the currently
 * featured wall with a large atmospheric preview, one emotional
 * excerpt, restrained microtype memory count, and two italic-serif
 * actions: "Replace this preserved night ✦" / "Retire quietly ✦".
 *
 * Below: a searchable drawer of eligible candidate events. Each
 * candidate card carries 2-3 polaroid previews + title + tiny
 * memory count + one handwritten excerpt + "preserved on {month
 * year}" + "Feature this night ✦".
 *
 * Quiet failure modes: ineligible candidates are hidden entirely.
 * Featured walls that have become ineligible since promotion show
 * a soft "This night is still settling ✦" placard instead of a
 * raw validation error.
 *
 * Protected — admin-only via the same `is_admin_user` server check
 * powering the rest of the admin surfaces. No discoverable nav
 * links outside admin surfaces.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const IVORY = '#FBEED0';
const GOLD = '#D4AF5E';
const GOLD_MUTED = 'rgba(212,170,82,0.78)';
const INK = '#3a2412';
const INK_DIM = 'rgba(58,36,14,0.66)';
const PARCHMENT = 'linear-gradient(180deg, rgba(248,238,214,0.96) 0%, rgba(240,224,192,0.96) 100%)';
const WALNUT = '#0B0604';

const AUDIENCE_ORDER = ['hub', 'year6', 'prom', 'any'];
const AUDIENCE_LABEL = {
  hub: 'Schools ✦',
  year6: 'Year 6 ✦',
  prom: 'Prom ✦',
  any: 'Universal fallback ✦',
};
const AUDIENCE_SUB = {
  hub: 'The /schools hub affordance',
  year6: 'The /year6 leavers landing',
  prom: 'The /prom landing',
  any: 'Used when an audience has no specific selection',
};

const REASON_COPY = {
  too_small: 'This night is still settling ✦',
  settling:  'This night is still settling ✦',
  moderated: 'This night is held aside ✦',
  missing:   'This night has been retired ✦',
};

function formatMonthYear(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
}

export default function AdminFeaturedDemosPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [current, setCurrent] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyAud, setBusyAud] = useState(null);
  // A quiet placard the curator briefly sees after a promotion —
  // fades into the freshly-filled lane like a card placed beside an
  // exhibit. Resets to null when the placard naturally decays.
  const [justPromotedAud, setJustPromotedAud] = useState(null);

  // Soft auth wall — anyone who isn't an admin is sent home quietly.
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/'); return; }
    // Admin verification is server-side; if the call 403s we redirect.
  }, [authLoading, user, navigate]);

  const loadCurrent = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/featured-demos`);
      setCurrent(r.data?.current || {});
    } catch (e) {
      if (e?.response?.status === 403) navigate('/');
    }
  }, [navigate]);

  const loadCandidates = useCallback(async (search) => {
    try {
      const url = `${API}/admin/featured-demos/candidates${search ? `?q=${encodeURIComponent(search)}` : ''}`;
      const r = await axios.get(url);
      setCandidates(r.data?.candidates || []);
    } catch {
      setCandidates([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([loadCurrent(), loadCandidates('')]);
      setLoading(false);
    })();
  }, [loadCurrent, loadCandidates]);

  useEffect(() => {
    const t = setTimeout(() => loadCandidates(q), 280);
    return () => clearTimeout(t);
  }, [q, loadCandidates]);

  const promote = async (audience, eventId) => {
    setBusyAud(audience);
    try {
      await axios.post(`${API}/admin/featured-demos`, { audience, event_id: eventId });
      await loadCurrent();
      // Quiet acknowledgement — the placard fades into the lane and
      // decays naturally. No toast, no animation burst, no success
      // state, no checkmark.
      setJustPromotedAud(audience);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('promote failed', e?.response?.data || e);
    } finally {
      setBusyAud(null);
    }
  };

  const retire = async (audience) => {
    setBusyAud(audience);
    try {
      await axios.post(`${API}/admin/featured-demos/retire`, { audience });
      await loadCurrent();
    } finally {
      setBusyAud(null);
    }
  };

  return (
    <div data-testid="admin-featured-demos" style={{ minHeight: '100vh', background: WALNUT, color: IVORY, fontFamily: SERIF }}>
      <Header onClose={() => navigate('/')} />

      <main style={{ maxWidth: 1280, margin: '0 auto', padding: 'clamp(48px, 6vw, 80px) clamp(20px, 4vw, 40px) 100px' }}>
        {/* Three (or four) editorial lanes */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 'clamp(18px, 2vw, 28px)',
            marginBottom: 64,
          }}
        >
          {AUDIENCE_ORDER.map((aud) => (
            <Lane
              key={aud}
              audience={aud}
              entry={current?.[aud]}
              busy={busyAud === aud}
              justPromoted={justPromotedAud === aud}
              onPlacardDecayed={() => setJustPromotedAud((cur) => (cur === aud ? null : cur))}
              onRetire={() => retire(aud)}
              onReplace={() => {
                const el = document.querySelector('[data-testid="curator-candidates"]');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            />
          ))}
        </div>

        {/* Searchable candidate drawer */}
        <CandidatesDrawer
          loading={loading}
          q={q}
          setQ={setQ}
          candidates={candidates}
          onFeature={(audience, eventId) => promote(audience, eventId)}
        />
      </main>
    </div>
  );
}

// ── Header ─────────────────────────────────────────────────────────
const Header = ({ onClose }) => (
  <header style={{
    position: 'sticky', top: 0, zIndex: 30,
    background: 'rgba(11,6,4,0.88)', backdropFilter: 'blur(14px)',
    borderBottom: '1px solid rgba(212,170,82,0.12)',
    padding: '18px 28px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  }}>
    <div>
      <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD_MUTED }}>
        THE CURATOR'S DESK ✦
      </div>
      <div style={{ marginTop: 4, fontFamily: SERIF, fontSize: 22, letterSpacing: '-0.005em', color: IVORY }}>
        Which nights represent the platform ✦
      </div>
    </div>
    <button
      type="button"
      onClick={onClose}
      data-testid="curator-close"
      style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 13, color: 'rgba(255,235,200,0.66)',
        background: 'transparent', border: 'none', cursor: 'pointer',
        textDecoration: 'underline', textDecorationColor: 'rgba(212,170,82,0.28)', textUnderlineOffset: 4,
      }}
    >
      Close the desk →
    </button>
  </header>
);

// PromotionPlacard — a curator quietly placing a card beside an
// exhibit. Per Craig: no toast, no animation burst, no success state,
// no checkmark. Just a small italic-serif placard that fades into
// the lane and decays naturally.
//
// Reads "Preserved as the {Audience} ✦ feature ✦" — same restraint
// vocabulary as the ArchiveReflection surface on /archive.
const PROMOTION_DWELL_MS = 2800;
const PROMOTION_FADE_IN_MS = 700;
const PROMOTION_FADE_OUT_MS = 1100;

const PromotionPlacard = ({ audience, onDecayed }) => {
  const [phase, setPhase] = useState('entering');
  const label = (AUDIENCE_LABEL[audience] || '').replace(' ✦', '');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('visible'), PROMOTION_FADE_IN_MS);
    const t2 = setTimeout(() => setPhase('fading'), PROMOTION_FADE_IN_MS + PROMOTION_DWELL_MS);
    const t3 = setTimeout(() => {
      if (onDecayed) onDecayed();
    }, PROMOTION_FADE_IN_MS + PROMOTION_DWELL_MS + PROMOTION_FADE_OUT_MS);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onDecayed]);

  const opacity = phase === 'entering' ? 0 : phase === 'visible' ? 1 : 0;
  const transition = phase === 'fading'
    ? `opacity ${PROMOTION_FADE_OUT_MS}ms ease-in, transform ${PROMOTION_FADE_OUT_MS}ms ease-in`
    : `opacity ${PROMOTION_FADE_IN_MS}ms ease-out, transform ${PROMOTION_FADE_IN_MS}ms ease-out`;

  return (
    <div
      data-testid={`curator-promotion-placard-${audience}`}
      aria-live="polite"
      style={{
        position: 'absolute',
        top: -14, left: 18, right: 18,
        padding: '8px 14px',
        background: 'linear-gradient(180deg, rgba(252,245,224,0.98) 0%, rgba(244,232,202,0.98) 100%)',
        border: '1px solid rgba(168,133,76,0.42)',
        borderRadius: 3,
        boxShadow: '0 16px 30px rgba(0,0,0,0.34), 0 0 0 1px rgba(168,133,76,0.10) inset',
        opacity,
        transform: phase === 'visible' ? 'translateY(0)' : 'translateY(-4px)',
        transition,
        pointerEvents: 'none',  // a placard is placed, never interacted with
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12.5, color: '#5a3818',
        textAlign: 'center',
        letterSpacing: '0.01em',
      }}
    >
      Preserved as the {label} ✦ feature ✦
    </div>
  );
};

// ── Lane ───────────────────────────────────────────────────────────
const Lane = ({ audience, entry, busy, justPromoted, onPlacardDecayed, onRetire, onReplace }) => {
  const settling = entry && !entry.eligible;
  const reason = settling ? (REASON_COPY[entry.reason_code] || REASON_COPY.settling) : null;
  return (
    <section
      data-testid={`curator-lane-${audience}`}
      style={{
        position: 'relative',
        background: PARCHMENT,
        borderRadius: 4,
        border: '1px solid rgba(168,133,76,0.30)',
        boxShadow: '0 30px 80px rgba(0,0,0,0.50), 0 0 0 1px rgba(168,133,76,0.10) inset',
        padding: '22px 22px 24px',
        color: INK,
        display: 'flex', flexDirection: 'column', minHeight: 340,
      }}
    >
      {justPromoted && entry && (
        <PromotionPlacard audience={audience} onDecayed={onPlacardDecayed} />
      )}
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.26em', textTransform: 'uppercase',
        color: 'rgba(168,133,76,0.95)', marginBottom: 6,
      }}>
        {AUDIENCE_LABEL[audience]}
      </div>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12.5, color: INK_DIM, marginBottom: 18,
      }}>
        {AUDIENCE_SUB[audience]}
      </div>

      {entry ? (
        <>
          <PolaroidStrip snaps={entry.sample_snaps || []} />
          <div style={{ marginTop: 18, fontFamily: SERIF, fontSize: 20, lineHeight: 1.2, color: INK }}>
            {entry.event_name}
          </div>
          {entry.one_excerpt && (
            <div style={{
              marginTop: 10,
              fontFamily: '"Caveat","Snell Roundhand",Georgia,serif',
              fontSize: 18, color: '#7a4a18', lineHeight: 1.2,
            }}>
              “{entry.one_excerpt}”
            </div>
          )}
          <div style={{
            marginTop: 12,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
            color: 'rgba(168,133,76,0.82)',
          }}>
            {entry.snap_count} memories · preserved on {formatMonthYear(entry.event_date || entry.first_seen_at || entry.curated_at)}
          </div>
          <PreviewNightLink slug={entry.slug} testId={`curator-preview-${audience}`} />
          {reason && (
            <div style={{
              marginTop: 14, padding: '8px 12px',
              fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5,
              color: '#7a4a18',
              background: 'rgba(168,133,76,0.10)',
              borderLeft: '2px solid rgba(168,133,76,0.50)',
            }}>
              {reason}
            </div>
          )}
          <div style={{ flex: 1 }} />
          <div style={{ marginTop: 22, display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
            <CuratorLink
              onClick={onReplace}
              testId={`curator-replace-${audience}`}
              disabled={busy}
            >
              Replace this preserved night ✦
            </CuratorLink>
            <CuratorLink
              onClick={onRetire}
              testId={`curator-retire-${audience}`}
              disabled={busy}
              dim
            >
              Retire quietly ✦
            </CuratorLink>
          </div>
        </>
      ) : (
        <EmptyLane audience={audience} onReplace={onReplace} />
      )}
    </section>
  );
};

const EmptyLane = ({ audience, onReplace }) => (
  <>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: 140,
      border: '1px dashed rgba(168,133,76,0.42)',
      background: 'rgba(168,133,76,0.04)',
      borderRadius: 3,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 13.5, color: '#7a4a18',
      textAlign: 'center', padding: '0 18px',
    }}>
      No night currently represents {AUDIENCE_LABEL[audience].replace(' ✦', '')}.
    </div>
    <div style={{ flex: 1 }} />
    <div style={{ marginTop: 22 }}>
      <CuratorLink onClick={onReplace} testId={`curator-replace-${audience}`}>
        Choose a night to feature ✦
      </CuratorLink>
    </div>
  </>
);

const PolaroidStrip = ({ snaps }) => {
  if (!snaps || snaps.length === 0) {
    return (
      <div style={{
        height: 110, borderRadius: 2,
        background: 'linear-gradient(160deg, rgba(168,133,76,0.10), rgba(60,38,18,0.10))',
        border: '1px dashed rgba(168,133,76,0.32)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, color: '#7a4a18',
      }}>
        — preview unavailable —
      </div>
    );
  }
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'flex-end', minHeight: 130 }}>
      {snaps.slice(0, 3).map((s, i) => {
        const tilt = i === 0 ? -5 : i === 2 ? 4 : -1;
        const lift = i === 1 ? -6 : 0;
        const url = s.photo_url && s.photo_url.startsWith('/api/')
          ? `${apiBase}${s.photo_url}`
          : s.photo_url;
        return (
          <div key={i} style={{
            transform: `rotate(${tilt}deg) translateY(${lift}px)`,
            background: '#FCF5E0',
            padding: '6px 6px 22px',
            boxShadow: '0 10px 22px rgba(60,38,18,0.32)',
            borderRadius: 1,
          }}>
            <div style={{
              width: 78, height: 90,
              backgroundImage: `url(${url})`,
              backgroundSize: 'cover', backgroundPosition: 'center',
              background: url ? `url(${url}) center/cover` : 'linear-gradient(180deg, #5a3818, #2a1808)',
              borderRadius: 1,
            }} />
          </div>
        );
      })}
    </div>
  );
};

const CuratorLink = ({ onClick, children, testId, disabled, dim }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 13.5, color: dim ? 'rgba(168,133,76,0.70)' : '#7a4a18',
      background: 'transparent', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
      padding: 0,
      textDecoration: 'underline', textDecorationColor: 'rgba(168,133,76,0.40)',
      textUnderlineOffset: 4,
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {children}
  </button>
);

// PreviewNightLink — quiet "step into the room" affordance.
//
// Per Craig: the curator's desk should support FEELING before
// selection. Opens the actual preserved wall via `/?demo={slug}` in
// a NEW TAB so the desk itself is never interrupted — scroll
// position, search query, and lane state are preserved by the
// browser's tab semantics with zero state machinery.
//
// The `from=curator` query param is reserved for future quieter
// landing variants (e.g., a curator-only atmospheric note, disabled
// share affordances). It is NEVER surfaced as a "curator mode" UI
// to non-admin visitors.
const PreviewNightLink = ({ slug, testId }) => {
  if (!slug) return null;
  return (
    <a
      href={`/?demo=${encodeURIComponent(slug)}&from=curator`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        marginTop: 10,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12.5,
        color: 'rgba(168,133,76,0.74)',
        textDecoration: 'none',
        transition: 'color 220ms ease, text-decoration-color 220ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = '#7a4a18';
        e.currentTarget.style.textDecoration = 'underline';
        e.currentTarget.style.textDecorationColor = 'rgba(168,133,76,0.42)';
        e.currentTarget.style.textUnderlineOffset = '4px';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = 'rgba(168,133,76,0.74)';
        e.currentTarget.style.textDecoration = 'none';
      }}
    >
      Preview this preserved night ✦
      <span aria-hidden style={{ fontSize: 10, opacity: 0.7 }}>↗</span>
    </a>
  );
};

// ── Candidate drawer ───────────────────────────────────────────────
const CandidatesDrawer = ({ loading, q, setQ, candidates, onFeature }) => (
  <section data-testid="curator-candidates" style={{
    marginTop: 20,
    padding: 'clamp(28px, 4vw, 48px) clamp(20px, 3vw, 36px) clamp(40px, 5vw, 64px)',
    background: 'rgba(248,238,214,0.04)',
    border: '1px solid rgba(212,170,82,0.16)',
    borderRadius: 4,
  }}>
    <div style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
      color: GOLD_MUTED, marginBottom: 8,
    }}>
      The selection room ✦
    </div>
    <div style={{
      fontFamily: SERIF, fontSize: 'clamp(20px, 2.4vw, 28px)',
      lineHeight: 1.18, color: IVORY, letterSpacing: '-0.005em',
      marginBottom: 18,
    }}>
      Eligible nights, gathered together
    </div>
    <input
      type="text"
      value={q}
      onChange={(e) => setQ(e.target.value)}
      placeholder="Search by name…"
      data-testid="curator-search"
      style={{
        width: '100%', maxWidth: 380, display: 'block',
        padding: '12px 14px',
        background: 'rgba(248,238,214,0.06)',
        border: '1px solid rgba(212,170,82,0.22)',
        borderRadius: 3,
        color: IVORY,
        fontFamily: SERIF, fontStyle: 'italic', fontSize: 14,
        outline: 'none',
        marginBottom: 28,
      }}
    />

    {loading ? (
      <div style={{ fontFamily: SERIF, fontStyle: 'italic', color: 'rgba(255,235,200,0.6)' }}>
        Gathering the room ✦
      </div>
    ) : candidates.length === 0 ? (
      <div style={{ fontFamily: SERIF, fontStyle: 'italic', color: 'rgba(255,235,200,0.6)' }}>
        No nights currently meet the editorial threshold ✦
      </div>
    ) : (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 'clamp(16px, 2vw, 24px)',
      }}>
        {candidates.map((c) => (
          <CandidateCard key={c.event_id} candidate={c} onFeature={onFeature} />
        ))}
      </div>
    )}
  </section>
);

const CandidateCard = ({ candidate, onFeature }) => {
  const [pickingFor, setPickingFor] = useState(null);
  return (
    <article
      data-testid={`curator-candidate-${candidate.event_id}`}
      style={{
        background: PARCHMENT,
        borderRadius: 3,
        border: '1px solid rgba(168,133,76,0.26)',
        padding: '18px 18px 20px',
        color: INK,
        boxShadow: '0 18px 42px rgba(0,0,0,0.36)',
      }}
    >
      <PolaroidStrip snaps={candidate.sample_snaps || []} />
      <div style={{
        marginTop: 14, fontFamily: SERIF, fontSize: 17, lineHeight: 1.2, color: INK,
      }}>
        {candidate.event_name || 'Untitled night'}
      </div>
      {candidate.one_excerpt && (
        <div style={{
          marginTop: 8,
          fontFamily: '"Caveat","Snell Roundhand",Georgia,serif',
          fontSize: 16, color: '#7a4a18', lineHeight: 1.2,
        }}>
          “{candidate.one_excerpt}”
        </div>
      )}
      <div style={{
        marginTop: 10,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
        color: 'rgba(168,133,76,0.82)',
      }}>
        {candidate.snap_count} memories · preserved on {formatMonthYear(candidate.event_date || candidate.created_at)}
      </div>
      <PreviewNightLink slug={candidate.slug} testId={`curator-preview-candidate-${candidate.event_id}`} />

      <div style={{ marginTop: 16 }}>
        {pickingFor ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, color: INK_DIM }}>
              Feature for:
            </span>
            {AUDIENCE_ORDER.map((aud) => (
              <button
                key={aud}
                type="button"
                onClick={() => { onFeature(aud, candidate.event_id); setPickingFor(null); }}
                data-testid={`curator-feature-${candidate.event_id}-${aud}`}
                style={{
                  fontFamily: SERIF, fontStyle: 'italic', fontSize: 12,
                  background: 'rgba(168,133,76,0.14)',
                  border: '1px solid rgba(168,133,76,0.38)',
                  borderRadius: 999, padding: '4px 10px',
                  color: '#5a3818', cursor: 'pointer',
                }}
              >
                {AUDIENCE_LABEL[aud].replace(' ✦', '')}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPickingFor(null)}
              style={{
                marginLeft: 'auto',
                fontFamily: SERIF, fontStyle: 'italic', fontSize: 11,
                color: INK_DIM, background: 'transparent', border: 'none', cursor: 'pointer',
                textDecoration: 'underline', textDecorationColor: 'rgba(168,133,76,0.32)',
              }}
            >
              cancel
            </button>
          </div>
        ) : (
          <CuratorLink
            onClick={() => setPickingFor(true)}
            testId={`curator-feature-open-${candidate.event_id}`}
          >
            Feature this night ✦
          </CuratorLink>
        )}
      </div>
    </article>
  );
};
